import { HttpParams } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AnalyticsService,
  ModalService,
  SearchComponent,
  SearchServiceToken,
  SimpleModalConfig,
} from '@frk/eds-components';
import {
  FundsSearchResponse,
  SearchFundsItem,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { FavoritesService } from '@products/services/favorites.service';
import {
  FtSearchOptions,
  FtSearchParams,
} from '@search/interfaces/search.interface';
import { ConfigService } from '@search/services/config.service';
import { FtSearchService } from '@search/services/ftsearch.service';
import { SiteConfigService } from '@services';
import { FundShareClassId, ShareClassName } from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { USServicingService } from '../services/us-servicing.service';

const logger = Logger.getLogger('AddtoWatchlistComponent');
const FUNDS = 'funds';

@Component({
  selector: 'ft-add-to-watchlist-modal',
  templateUrl: './add-to-watchlist-modal.component.html',
})
export class AddToWatchlistModalComponent implements OnInit, OnDestroy {
  public isVisible$: Observable<boolean>;
  public modalConfig: SimpleModalConfig;
  public showModal = false;
  @ViewChild('onPageSearch', { static: false }) onPageSearch: SearchComponent;
  private componentDestroyed$: Subject<boolean> = new Subject();
  public searchItems: SearchFundsItem[] = [];
  public hasError = false;
  public isSearchEmpty = false;
  private debouncer$: Subject<FtSearchParams> = new Subject<FtSearchParams>();
  private page = 0;
  public isViewMoreBtnVisible = true;
  public isProductAlreadyExist = false;
  private searchOptions: FtSearchOptions = {
    page: 0,
    term: '',
    dataSource: 'dev',
    config: {
      ftSearchUrl: '',
      ftInsightsUrl: '',
      ftAutoCompleteUrl: '',
      httpParams: new HttpParams(),
      httpOptions: {},
    },
    counters: {
      funds: 100,
      literature: 4,
      pages: 4,
    },
  };

  @Input() brLabels: object = {};
  constructor(
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
    private usServicingService: USServicingService,
    private modalService: ModalService,
    private favoriteService: FavoritesService,
    private analyticsService: AnalyticsService,
    @Inject(SearchServiceToken) private searchService: FtSearchService,
    private siteConfigService: SiteConfigService
  ) {
    this.searchService.onPageSearchOutput$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        ({ results, error }) => {
          // error handling without unsubscribing from observable
          if (error) {
            this.hasError = true;
            logger.debug('error in search response');
            return;
          }

          if (!Array.isArray(results)) {
            const fundResults: FundsSearchResponse = (results as unknown) as FundsSearchResponse;

            const totalNumberOfFunds = fundResults.response?.hits?.hits.length;

            this.isViewMoreBtnVisible = totalNumberOfFunds === 100 || false;

            if (this.page === 0) {
              this.searchItems = this.usServicingService.getFundsFromResult(
                fundResults
              );
              this.isProductAlreadyExist =
                totalNumberOfFunds > 0 && this.searchItems?.length === 0;
            } else {
              this.searchItems = [
                ...this.searchItems,
                ...this.usServicingService.getFundsFromResult(fundResults),
              ];
            }

            this.isSearchEmpty = this.searchItems?.length === 0 || false;

            logger.debug('search result from elastic', this.searchItems);
          }
          this.cd.markForCheck();
        },
        () => {
          this.hasError = true;
        }
      );

    // ignore search term if new is called within 500ms
    // debouncer is triggered after search term
    this.debouncer$
      .pipe(takeUntil(this.componentDestroyed$), debounceTime(500))
      .subscribe((searchParams: FtSearchParams) =>
        this.searchService.findAFund(searchParams)
      );
  }

  ngOnInit(): void {
    this.modalConfig = {
      modalId: 'watchlistModal',
      type: 'simple',
    };
    this.configService
      .getConfig('')
      ?.pipe(takeUntil(this.componentDestroyed$))
      .subscribe((config) => {
        this.searchOptions.config = config;
      });
  }

  /**
   * search entered string
   */
  public findAFund(term: string) {
    if (term === '') {
      this.onClearResults();
    } else {
      this.hasError = false;
      this.isSearchEmpty = false;
      this.isProductAlreadyExist = false;
      this.searchOptions.term = term;
      this.searchOptions.page = this.page;
      this.searchOptions.collection = FUNDS;

      const searchParams: FtSearchParams = {
        options: this.searchOptions,
        filters: JSON.stringify([
          {
            fieldName: 'producttype.exact',
            fieldValue: this.siteConfigService.getUSServicingProducts(),
          },
        ]),
      };

      this.debouncer$.next(searchParams);
    }
  }

  /**
   * add to watchlist
   */
  /**
   * add to watchlist
   */
  public addToWatchlist(
    fund: SearchFundsItem,
    fundShareClassId: FundShareClassId,
    shareClass: ShareClassName
  ) {
    // track UA and GA4 event in the single event for add focus list
    this.analyticsService.trackEvent({
      event: 'add_product_list',
      detailed_event: 'Add Product List',
      fund_name: fund.fundName,
      event_data: {
        cart_type: 'focus list',
        fund_class: shareClass,
        fund_name: fund.fundName,
        fund_number: Number(fund.fundId),
        fund_ticker: fund.ticker,
      },
    });

    this.favoriteService.toggleFavorite(fundShareClassId, fund.fundName);
    this.showModal = false;
    this.onClearResults();
    this.modalService.close(this.modalConfig.modalId);
  }

  ngOnDestroy(): void {
    // unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  /**
   * on closing the modal
   */
  public handleCloseModal() {
    this.onClearResults();
    this.showModal = false;
  }

  /**
   * clear the searched string
   */
  private onClearResults() {
    this.searchOptions.term = '';
    this.searchItems = [];
    this.onPageSearch.value = '';
    this.onPageSearch.hasTyped = false;
    this.isSearchEmpty = false;
    this.isProductAlreadyExist = false;
    this.hasError = false;
    this.page = 0;
  }
  /**
   * we want to load more funds and
   * to call search api
   */
  public viewMore() {
    this.page += 1;
    this.findAFund(this.searchOptions.term);
  }
}
